// src/components/RequireAuth.js
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isAuthenticated, getLogoutRedirectUrl } from '../utils/authUtils';

/**
 * RequireAuth - Protected Route Wrapper
 *
 * Ensures users are authenticated before accessing any route.
 * If not authenticated, redirects to login page and saves current URL
 * for post-login redirect.
 *
 * Usage:
 *   <Route path="/view" element={<RequireAuth><ViewCameras /></RequireAuth>} />
 */
const RequireAuth = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      console.log('[RequireAuth] User not authenticated, redirecting to login');

      // Save current path for redirect after login
      const currentPath = location.pathname + location.search;
      try {
        localStorage.setItem('redirectAfterLogin', currentPath);
        console.log('[RequireAuth] Saved redirect path:', currentPath);
      } catch (error) {
        console.warn('[RequireAuth] Failed to save redirect path:', error);
      }

      // Get login page URL (localhost:3001 or whitetiger.ai)
      const loginUrl = getLogoutRedirectUrl();
      console.log('[RequireAuth] Redirecting to login:', loginUrl);

      // Redirect to login page
      window.location.href = loginUrl;
    }
  }, [location]);

  // If not authenticated, show loading state while redirecting
  if (!isAuthenticated()) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <div style={{ marginBottom: '1rem' }}>Authentication required</div>
        <div style={{ fontSize: '14px', color: '#999' }}>Redirecting to login...</div>
      </div>
    );
  }

  // User is authenticated, render the protected route
  return children;
};

export default RequireAuth;
